import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, AlertTriangle, FileText, CreditCard, Clock, Ban, CheckCircle } from 'lucide-react';

const TermsOfService = () => {
  const lastUpdated = "May 21, 2026";
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900/20 to-gray-900">
      {/* Header */}
      <div className="bg-black/50 backdrop-blur-lg border-b border-purple-500/20 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <Link to="/" className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-600/20 rounded-full mb-6">
            <FileText className="w-8 h-8 text-purple-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Terms of Service</h1>
          <p className="text-gray-400">Last Updated: {lastUpdated}</p>
        </div>

        {/* Important Notice Banner */}
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-red-400 font-bold text-lg mb-2">IMPORTANT: READ BEFORE BOOKING</h3>
              <p className="text-gray-300">
                By booking a session or purchasing any service from High Quality Studios, you acknowledge that you have read, 
                understood, and agree to be bound by these Terms of Service. <strong className="text-white">ALL SALES ARE FINAL.</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-8">
          
          {/* Section 1: Acceptance of Terms */}
          <section className="bg-gray-800/50 rounded-xl p-6 border border-purple-500/20">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">1</span>
              Acceptance of Terms
            </h2>
            <div className="text-gray-300 space-y-4">
              <p>
                Welcome to High Quality Studios ("Company," "we," "us," or "our"). These Terms of Service ("Terms") govern your 
                access to and use of our website, services, and any related applications (collectively, the "Services").
              </p>
              <p>
                By accessing or using our Services, making a booking, or purchasing any products or services, you agree to be 
                bound by these Terms. If you do not agree to these Terms, you may not access or use our Services.
              </p>
              <p>
                We reserve the right to modify these Terms at any time. Your continued use of the Services after any changes 
                constitutes your acceptance of the new Terms.
              </p>
            </div>
          </section>

          {/* Section 2: Services Description */}
          <section className="bg-gray-800/50 rounded-xl p-6 border border-purple-500/20">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">2</span>
              Services Description
            </h2>
            <div className="text-gray-300 space-y-4">
              <p>High Quality Studios provides professional recording studio services including but not limited to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Recording sessions (audio and video)</li>
                <li>Mixing and mastering services</li>
                <li>Beat production and sales</li>
                <li>Live streaming services</li>
                <li>Visual production and content creation</li>
                <li>Engineering services</li>
              </ul>
            </div>
          </section>

          {/* Section 3: REFUND POLICY - HIGHLIGHTED */}
          <section className="bg-gradient-to-r from-red-900/30 to-orange-900/30 rounded-xl p-6 border-2 border-red-500/50">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Ban className="w-8 h-8 text-red-400" />
              NO REFUND POLICY
            </h2>
            <div className="text-gray-200 space-y-4">
              <div className="bg-black/30 rounded-lg p-4 border border-red-500/30">
                <p className="text-xl font-bold text-red-400 mb-2">
                  ⚠️ ALL SALES ARE FINAL. NO REFUNDS ONCE SERVICES ARE RENDERED OR DIGITAL FILES ARE DELIVERED.
                </p>
              </div>
              
              <p className="font-semibold text-white">By completing a booking or purchase, you expressly acknowledge and agree that:</p>
              
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                  <span><strong>No Refunds for Completed Sessions:</strong> Once a recording session has begun or been completed, no refunds will be issued under any circumstances.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                  <span><strong>No Refunds for Digital Deliverables:</strong> Once any digital files, beats, mixes, masters, or other digital content have been delivered or made available for download, the sale is final and non-refundable.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                  <span><strong>No Refunds for Deposits:</strong> All deposits are non-refundable and serve to secure your booking time slot.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                  <span><strong>No Refunds for No-Shows:</strong> If you fail to appear for your scheduled session without proper cancellation notice (minimum 24 hours), you forfeit your entire payment.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                  <span><strong>No Refunds for Dissatisfaction:</strong> Subjective dissatisfaction with the creative outcome does not entitle you to a refund. Our engineers provide professional services to the best of their abilities.</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 4: Chargeback Policy */}
          <section className="bg-gradient-to-r from-yellow-900/30 to-red-900/30 rounded-xl p-6 border-2 border-yellow-500/50">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <CreditCard className="w-8 h-8 text-yellow-400" />
              Chargeback & Dispute Policy
            </h2>
            <div className="text-gray-200 space-y-4">
              <div className="bg-black/30 rounded-lg p-4 border border-yellow-500/30">
                <p className="text-lg font-bold text-yellow-400">
                  ⚠️ CHARGEBACKS ARE CONSIDERED FRAUDULENT ACTIVITY AND WILL BE CONTESTED
                </p>
              </div>
              
              <p>By making a purchase, you agree to the following chargeback and dispute terms:</p>
              
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-1" />
                  <span><strong>Prohibited Chargebacks:</strong> Filing a chargeback or payment dispute after receiving services or digital files constitutes a breach of this agreement and may be considered fraud.</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-1" />
                  <span><strong>Evidence Retention:</strong> We maintain comprehensive records of all transactions, communications, session logs, file deliveries, and signed agreements. All chargebacks will be contested with full documentation.</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-1" />
                  <span><strong>Collection Actions:</strong> In the event of a successful fraudulent chargeback, we reserve the right to pursue collection of the owed amount plus any associated fees, including but not limited to chargeback fees, legal fees, and collection costs.</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-1" />
                  <span><strong>Service Suspension:</strong> Any client who initiates a chargeback will be permanently banned from all future services and may be reported to fraud prevention databases.</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-1" />
                  <span><strong>Legal Action:</strong> We reserve the right to pursue legal action for fraudulent chargebacks, including claims for the original amount, consequential damages, and attorney's fees.</span>
                </li>
              </ul>
              
              <div className="bg-yellow-500/10 rounded-lg p-4 mt-4">
                <p className="text-yellow-300 font-medium">
                  <strong>Dispute Resolution:</strong> If you have any issues with your purchase, please contact us directly at 
                  <a href="mailto:support@hqstu.com" className="text-yellow-400 underline ml-1">support@hqstu.com</a> before 
                  contacting your payment provider. We are committed to resolving legitimate concerns professionally.
                </p>
              </div>
            </div>
          </section>

          {/* Section 5: Booking & Cancellation */}
          <section className="bg-gray-800/50 rounded-xl p-6 border border-purple-500/20">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Clock className="w-8 h-8 text-purple-400" />
              Booking & Cancellation Policy
            </h2>
            <div className="text-gray-300 space-y-4">
              <h3 className="text-lg font-semibold text-white">Booking Requirements:</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>A deposit is required to secure all bookings</li>
                <li>Remaining balance is due at the start of your session</li>
                <li>Valid identification may be required at check-in</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-white mt-6">Cancellation Terms:</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>24+ Hours Notice:</strong> Session may be rescheduled one time (subject to availability). Deposit is non-refundable but may be applied to rescheduled session.</li>
                <li><strong>Less than 24 Hours Notice:</strong> Full payment is forfeited. No rescheduling.</li>
                <li><strong>No-Show:</strong> Full payment is forfeited. Client will be flagged for future bookings.</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-white mt-6">Late Arrivals:</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Sessions begin at the scheduled time regardless of your arrival</li>
                <li>Late arrivals do not extend your session time</li>
                <li>Arrivals more than 30 minutes late may result in session cancellation with no refund</li>
              </ul>
            </div>
          </section>

          {/* Section 6: Payment Terms */}
          <section className="bg-gray-800/50 rounded-xl p-6 border border-purple-500/20">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">6</span>
              Payment Terms
            </h2>
            <div className="text-gray-300 space-y-4">
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" />
                  <span>All prices are in US Dollars (USD) unless otherwise specified</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" />
                  <span>Payment is processed securely through Stripe</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" />
                  <span>By providing payment information, you authorize us to charge the agreed-upon amount</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" />
                  <span>Additional services requested during a session will be charged at current rates</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" />
                  <span>Promotional discounts cannot be combined and are subject to terms</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 6A: SMS / Text Messaging Consent */}
          <section className="bg-gray-800/50 rounded-xl p-6 border border-emerald-500/30">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="bg-emerald-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">6a</span>
              SMS / Text Messaging Consent
            </h2>
            <div className="text-gray-300 space-y-4">
              <p>
                By providing your mobile phone number on our booking form and agreeing to these Terms,
                you expressly consent to receive recurring transactional text messages from
                High Quality Studios LLC, including:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Booking confirmation after payment</li>
                <li>Booking approval or decline notices from your assigned engineer</li>
                <li>Appointment reminders 24 hours and 3 hours before each session</li>
                <li>Occasional promotional offers to past customers (e.g., deals, specials)</li>
              </ul>
              <p>
                <strong className="text-white">Message frequency varies</strong> based on your booking
                activity, typically 2–6 messages per booking.{' '}
                <strong className="text-white">Message and data rates may apply</strong> depending on
                your mobile carrier.
              </p>
              <p>
                <strong className="text-white">Opt out at any time</strong> by replying{' '}
                <span className="bg-emerald-700/40 text-emerald-200 px-1.5 py-0.5 rounded font-mono">STOP</span>{' '}
                to any text from us. To resume messages, reply{' '}
                <span className="bg-emerald-700/40 text-emerald-200 px-1.5 py-0.5 rounded font-mono">START</span>.
                For help, reply <span className="bg-emerald-700/40 text-emerald-200 px-1.5 py-0.5 rounded font-mono">HELP</span>{' '}
                or email <a href="mailto:trapspade@hqstu.com" className="text-emerald-300 underline">trapspade@hqstu.com</a>.
              </p>
              <p>
                We do not share, sell, rent, or lease your phone number or SMS opt-in data to any
                third party for marketing purposes. Mobile carriers (T-Mobile, Verizon, AT&amp;T, etc.)
                are not liable for delayed or undelivered messages. Full SMS terms are in our{' '}
                <Link to="/privacy" className="text-emerald-300 underline">Privacy Policy</Link>.
              </p>
            </div>
          </section>

          {/* Section 7: Intellectual Property */}
          <section className="bg-gray-800/50 rounded-xl p-6 border border-purple-500/20">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">7</span>
              Intellectual Property & Ownership
            </h2>
            <div className="text-gray-300 space-y-4">
              <p><strong>Client Content:</strong> You retain ownership of your original creative content (lyrics, melodies, compositions) that you bring to sessions.</p>
              
              <p><strong>Studio-Produced Content:</strong> Ownership of beats, instrumentals, and production elements created by High Quality Studios engineers depends on the specific licensing agreement purchased.</p>
              
              <p><strong>Work Product:</strong> Upon full payment, you receive a license to use the recorded and mixed/mastered files according to the terms of your service agreement.</p>
              
              <p><strong>Portfolio Rights:</strong> High Quality Studios reserves the right to use portions of sessions for promotional purposes, portfolio demonstration, and social media content unless otherwise agreed in writing.</p>
            </div>
          </section>

          {/* Section 8: Studio Rules */}
          <section className="bg-gray-800/50 rounded-xl p-6 border border-purple-500/20">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">8</span>
              Studio Rules & Conduct
            </h2>
            <div className="text-gray-300 space-y-4">
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>No illegal substances or activities on premises</li>
                <li>Limited guests allowed (typically 2-3 per session unless otherwise arranged)</li>
                <li>No weapons of any kind</li>
                <li>Respect studio equipment - damage will be charged to the client</li>
                <li>No recording of studio premises or staff without permission</li>
                <li>Maintain professional and respectful conduct at all times</li>
              </ul>
              <p className="mt-4 text-yellow-400">
                <strong>Violation of studio rules may result in immediate session termination without refund and permanent ban from services.</strong>
              </p>
            </div>
          </section>

          {/* Section 9: Limitation of Liability */}
          <section className="bg-gray-800/50 rounded-xl p-6 border border-purple-500/20">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Shield className="w-8 h-8 text-purple-400" />
              Limitation of Liability
            </h2>
            <div className="text-gray-300 space-y-4">
              <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>High Quality Studios shall not be liable for any indirect, incidental, special, consequential, or punitive damages</li>
                <li>Our total liability shall not exceed the amount paid for the specific service in question</li>
                <li>We are not responsible for any lost revenue, lost profits, or lost opportunities resulting from the use or inability to use our services</li>
                <li>We are not liable for equipment failure, technical difficulties, or circumstances beyond our reasonable control</li>
                <li>Client is responsible for backing up their own files - we are not liable for data loss</li>
              </ul>
            </div>
          </section>

          {/* Section 10: Indemnification */}
          <section className="bg-gray-800/50 rounded-xl p-6 border border-purple-500/20">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">10</span>
              Indemnification
            </h2>
            <div className="text-gray-300 space-y-4">
              <p>
                You agree to indemnify, defend, and hold harmless High Quality Studios, its owners, employees, contractors, 
                and affiliates from any claims, damages, losses, liabilities, costs, and expenses (including attorney's fees) 
                arising from:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Your use of our Services</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any rights of third parties</li>
                <li>Any content you provide or create during sessions</li>
                <li>Any fraudulent chargeback or payment dispute</li>
              </ul>
            </div>
          </section>

          {/* Section 11: Dispute Resolution */}
          <section className="bg-gray-800/50 rounded-xl p-6 border border-purple-500/20">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">11</span>
              Dispute Resolution & Governing Law
            </h2>
            <div className="text-gray-300 space-y-4">
              <p>
                <strong>Governing Law:</strong> These Terms shall be governed by and construed in accordance with the laws of 
                the State of New York, without regard to its conflict of law provisions.
              </p>
              <p>
                <strong>Jurisdiction:</strong> Any disputes arising under these Terms shall be subject to the exclusive 
                jurisdiction of the courts located in New York, NY.
              </p>
              <p>
                <strong>Informal Resolution:</strong> Before filing any formal legal claim, you agree to first contact us 
                to attempt to resolve the dispute informally.
              </p>
            </div>
          </section>

          {/* Section 12: Contact Information */}
          <section className="bg-gray-800/50 rounded-xl p-6 border border-purple-500/20">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">12</span>
              Contact Information
            </h2>
            <div className="text-gray-300 space-y-4">
              <p>For questions about these Terms of Service, please contact us:</p>
              <div className="bg-black/30 rounded-lg p-4">
                <p><strong>High Quality Studios</strong></p>
                <p>Email: <a href="mailto:support@hqstu.com" className="text-purple-400 hover:text-purple-300">support@hqstu.com</a></p>
                <p>Website: <a href="https://hqstu.com" className="text-purple-400 hover:text-purple-300">hqstu.com</a></p>
              </div>
            </div>
          </section>

          {/* Agreement Acknowledgment */}
          <section className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 rounded-xl p-6 border border-purple-500/30 mt-8">
            <div className="text-center">
              <h3 className="text-xl font-bold text-white mb-4">Acknowledgment of Agreement</h3>
              <p className="text-gray-300 mb-4">
                By using our services, making a booking, or completing a purchase, you acknowledge that you have read, 
                understood, and agree to be bound by these Terms of Service, including the No Refund Policy and 
                Chargeback Policy stated herein.
              </p>
              <p className="text-purple-400 font-semibold">
                YOUR USE OF OUR SERVICES CONSTITUTES YOUR ACCEPTANCE OF THESE TERMS.
              </p>
            </div>
          </section>

        </div>

        {/* Back to Top / Home */}
        <div className="text-center mt-12">
          <Link 
            to="/" 
            className="inline-flex items-center px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
